"use client";

import * as React from "react";
import { useRouter } from "next/navigation";

import { cn } from "@/lib/utils";
import { Button } from "./ui/button";
import {
  Drawer,
  DrawerContent,
  DrawerDescription,
  DrawerTitle,
  DrawerTrigger,
} from "./ui/drawer";
import { NavbarItem } from "./navbar";
import { ChevronDown, Menu } from "lucide-react";
import IconText from "./icon-text";
import { useLocale, useTranslations } from "next-intl";
import { typographyClass } from "./typography";
import { Accordion, AccordionContent, AccordionItem } from "./ui/accordion";
import { Link } from "./link";
import * as AccordionPrimitive from "@radix-ui/react-accordion";
import LanguageSwitcher from "./language-switcher";
import SmoothScrolling from "./smooth-scrolling";

export function MobileNavbar({ items }: { items: NavbarItem[] }) {
  const t = useTranslations("navbar");
  const locale = useLocale();

  const [open, setOpen] = React.useState(false);

  const onOpenChange = React.useCallback(
    (open: boolean) => {
      setOpen(open);
    },
    [setOpen]
  );

  return (
    <Drawer open={open} onOpenChange={onOpenChange}>
      <IconText className="lg:hidden" />
      <div className="flex items-center justify-center gap-8">
        <LanguageSwitcher />
        <DrawerTrigger asChild>
          <Button
            variant="ghost"
            className="h-10 w-10 gap-4 px-0 text-base hover:bg-transparent focus-visible:bg-transparent focus-visible:ring-0 focus-visible:ring-offset-0 lg:hidden"
          >
            <Menu size={40} className="!h-10 !w-10" />
            <span className="sr-only">{t("toggleMenu")}</span>
          </Button>
        </DrawerTrigger>
        <DrawerContent className="max-h-[80svh] bg-card border-card text-card-foreground">
          <DrawerTitle
            className={cn(typographyClass.smallSubtitle, "p-6 pb-2 sr-only")}
          >
            {t("usefulLinks")}
          </DrawerTitle>
          <DrawerDescription className="hidden">
            {t("navbar")}
          </DrawerDescription>
          <nav aria-label="Main" data-orientation="vertical" className="overflow-auto" >
            <ul className="flex flex-col space-y-2 p-6 pb-20 h-full">
              {items.map((item, index) => (
                <li key={index} className="flex flex-col gap-4 pt-6">
                  {item.href ? (
                    <Link
                      href={item.href![locale as keyof typeof item.href]}
                      onClick={() => {
                        onOpenChange?.(false);
                      }}
                      className={cn(
                        typographyClass.largeBody,
                        "font-medium justify-start"
                      )}
                      title={item.label}
                    >
                      {item.label}
                    </Link>
                  ) : (
                    <Accordion type="single" collapsible>
                      <AccordionItem
                        value={`item-${index}`}
                        className="border-0"
                      >
                        <AccordionPrimitive.Header asChild>
                          <div>
                            <AccordionPrimitive.Trigger
                              className={cn(
                                typographyClass.largeBody,
                                "w-full flex flex-1 items-center justify-between py-4 transition-all [&[data-state=open]>svg]:rotate-180",
                                "p-0 font-medium"
                              )}
                            >
                              {item.label}
                              <ChevronDown className="h-4 w-4 shrink-0 text-muted-foreground transition-transform duration-200" />
                            </AccordionPrimitive.Trigger>
                          </div>
                        </AccordionPrimitive.Header>

                        <AccordionContent className="flex flex-col gap-2 pt-4 pb-0">
                          <ul>
                            {item?.children?.length &&
                              item.children.map((item) => (
                                <li
                                  key={
                                    item.href![locale as keyof typeof item.href]
                                  }
                                >
                                  <Link
                                    onClick={() => {
                                      onOpenChange?.(false);
                                    }}
                                    title={item.label}
                                    href={
                                      item.href![
                                        locale as keyof typeof item.href
                                      ]
                                    }
                                    className={cn(
                                      typographyClass.largeBody,
                                      "opacity-80 justify-start"
                                    )}
                                  >
                                    {item.label}
                                  </Link>
                                </li>
                              ))}
                          </ul>
                        </AccordionContent>
                      </AccordionItem>
                    </Accordion>
                  )}
                </li>
              ))}
            </ul>
          </nav>
        </DrawerContent>
      </div>
    </Drawer>
  );
}
